import * as c from "@yahshi/contracts";
import { createDevelopmentMembershipApi as createConfigurationApi, seedMemberships, type DevelopmentMembershipApi } from "./data/developmentMembershipApi";
import { seedAccounts } from "../m7-points/data/developmentPointApi";
import type { PointApi } from "../m7-points/data/PointApi";
import { createDevelopmentMembershipApi as createAssetApi, type DevelopmentAssetData } from "./developmentApi";

type Options = Parameters<typeof createConfigurationApi>[0] & {
  getApps: () => Promise<{ id: string; name: string; status: string }[]>;
};
type Lot = {
  poolId: string; userId: string; assetKey: string; quantity: number; remaining: number;
  startsAt: string; expiresAt: string | null; revoked: boolean; level: number; benefits: string[];
};
type Owner = { poolId: string; userId: string; assetKey: string; frozen: boolean; debt: number; createdAt: string };
type Entry = c.AssetLedger & { balanceAfter: number | null };
const now = () => new Date().toISOString();
const clone = <T,>(value: T): T => structuredClone(value);

/** In-memory acceptance sandbox. All three UI APIs project this one store.
 * WeChat settlement/refunds remain unavailable here; PostgreSQL verifies money flows.
 */
export function createUnifiedDevelopmentMembershipApis(options: Options) {
  const configuration = createConfigurationApi(options);
  const pools: c.AssetPool[] = [], bindings: c.AssetBinding[] = [], products: c.AssetProduct[] = [];
  const owners: Owner[] = [], lots: Lot[] = [], entries: Entry[] = [];
  const receipts = new Map<string, string>();
  const users = new Map<string, { name: string; publicId: string }>();
  const oldIds = new Map<string, string>();
  const seededEvents = new Map<string, c.MembershipEvent[]>();
  for (const { membership: m, events } of seedMemberships) {
    users.set(m.userId, { name: m.userName, publicId: m.userPublicId });
    oldIds.set(`${m.appId}:${m.userId}`, m.id);
    seededEvents.set(m.id, clone(events));
  }
  for (const { account: a } of seedAccounts) users.set(a.userId, { name: a.userName, publicId: a.userPublicId });
  for (const [appId, appName] of [["app_yahshi_web", "Yahshi Web"], ["app_yahshi_admin", "Yahshi Admin"]]) {
    for (const kind of ["membership", "points"] as const) {
      const pool = { id: crypto.randomUUID(), name: `${appName} · ${kind}`, nameUg: `${appName} · ${kind}`, kind, enabled: true, sandbox: false, version: 1, createdAt: now() };
      pools.push(pool);
      bindings.push({ appId: appId!, appName: appName!, kind, poolId: pool.id, poolName: pool.name, poolNameUg: pool.nameUg, version: 1 });
    }
  }
  const bound = (appId: string, kind: c.AssetPool["kind"]) => {
    const b = bindings.find((b) => b.appId === appId && b.kind === kind);
    const p = pools.find((p) => p.id === b?.poolId && p.enabled);
    if (!p) throw new Error("请先在应用关联中配置并启用对应权益空间");
    return p;
  };
  const owner = (poolId: string, userId: string, assetKey: string) => {
    if (!users.has(userId)) throw new Error("用户不存在");
    let row = owners.find((a) => a.poolId === poolId && a.userId === userId && a.assetKey === assetKey);
    if (!row) { row = { poolId, userId, assetKey, frozen: false, debt: 0, createdAt: now() }; owners.push(row); }
    return row;
  };
  const account = (a: Owner): c.AssetAccount => {
    const p = pools.find((p) => p.id === a.poolId)!;
    const alive = lots.filter((l) => l.poolId === a.poolId && l.userId === a.userId && l.assetKey === a.assetKey && !l.revoked && l.remaining > 0 && (!l.expiresAt || l.expiresAt > now()));
    const current = alive.filter((l) => l.startsAt <= now());
    const permanent = alive.some((l) => l.expiresAt === null);
    return { poolId: p.id, poolName: p.name, poolNameUg: p.nameUg, sandbox: p.sandbox, kind: p.kind, userId: a.userId, userName: users.get(a.userId)!.name,
      assetKey: a.assetKey, available: a.frozen || a.debt > 0 || p.kind === "membership" ? 0 : current.reduce((sum, l) => sum + l.remaining, 0),
      frozen: a.frozen, debt: a.debt, active: !a.frozen && a.debt === 0 && current.length > 0,
      level: Math.max(0, ...current.map((l) => l.level)), permanent,
      expiresAt: permanent ? null : alive.map((l) => l.expiresAt!).sort().at(-1) ?? null,
      benefits: a.frozen || a.debt > 0 ? [] : [...new Set(current.flatMap((l) => l.benefits))] };
  };
  const post = (a: Owner, appId: string, action: string, quantity: number, reason: string) => {
    const row: Entry = { id: crypto.randomUUID(), poolId: a.poolId, userId: a.userId, appId, assetKey: a.assetKey, action, quantity,
      reason, referenceId: null, actor: "本地演示管理员", createdAt: now(), balanceAfter: account(a).available };
    entries.unshift(row);
    return row;
  };
  const adjust = (input: c.AssetAdjustment): c.AssetAccount => {
    const v = c.assetAdjustmentSchema.parse(input);
    const pool = pools.find((p) => p.id === v.poolId);
    if (!pool || bound(v.appId, pool.kind).id !== pool.id) throw new Error("权益空间类型不匹配");
    if (v.action === "grant") c.assetProductInputSchema.parse({ mutationId: v.mutationId, reason: v.reason, poolId: v.poolId,
      name: "管理员赠送", kind: pool.kind, assetKey: v.assetKey, priceMinor: 1, pointsPrice: null,
      quantity: v.quantity, durationDays: v.durationDays, level: v.level, permanent: v.permanent,
      consumable: v.consumable, benefits: v.benefits, status: "draft" });
    const a = owner(v.poolId, v.userId, v.assetKey);
    const ownLots = lots.filter((l) => l.poolId === a.poolId && l.userId === a.userId && l.assetKey === a.assetKey);
    if (v.action === "grant") {
      const definition = ownLots[0];
      if (definition && definition.level !== v.level) throw new Error("同一资产键的等级必须保持一致");
      const startsAt = pool.kind === "membership" && !v.permanent
        ? ownLots.filter((l) => !l.revoked && l.expiresAt && l.expiresAt > now()).reduce((end, l) => l.expiresAt! > end ? l.expiresAt! : end, now()) : now();
      const quantity = pool.kind === "membership" ? v.permanent ? 1 : v.durationDays! * 86400 : v.quantity;
      const settled = pool.kind === "membership" ? 0 : Math.min(a.debt, quantity);
      a.debt -= settled;
      lots.push({ poolId: a.poolId, userId: a.userId, assetKey: a.assetKey, quantity, remaining: quantity - settled, startsAt,
        expiresAt: v.permanent || !v.durationDays ? null : new Date(Date.parse(startsAt) + v.durationDays * 86400000).toISOString(), revoked: false, level: v.level, benefits: v.benefits });
      post(a, v.appId, "grant", quantity, v.reason);
      if (settled) post(a, v.appId, "debt_settlement", -settled, v.reason);
    } else if (v.action === "debit") {
      if (pool.kind === "membership") throw new Error("会员不能按数量消费");
      if (a.frozen) throw new Error("资产账户已冻结");
      if (a.debt) throw new Error("请先清偿资产欠账");
      if (account(a).available < v.quantity) throw new Error("积分余额不足");
      let need = v.quantity;
      for (const l of ownLots.filter((l) => !l.revoked && l.startsAt <= now() && (!l.expiresAt || l.expiresAt > now())).sort((a, b) => (a.expiresAt ?? "z").localeCompare(b.expiresAt ?? "z"))) {
        const take = Math.min(need, l.remaining); l.remaining -= take; need -= take; if (!need) break;
      }
      post(a, v.appId, "consume", -v.quantity, v.reason);
    } else if (v.action === "revoke") {
      if (pool.kind !== "membership") throw new Error("仅会员权益可以取消");
      for (const l of ownLots) { l.revoked = true; l.remaining = 0; }
      post(a, v.appId, "revoke", 0, v.reason);
    } else {
      a.frozen = v.action === "freeze";
      post(a, v.appId, v.action, 0, v.reason);
    }
    return account(a);
  };
  for (const { account: a, entries: history } of seedAccounts) {
    const p = bound(a.appId, "points"), own = owner(p.id, a.userId, "points");
    lots.push({ poolId: p.id, userId: a.userId, assetKey: "points", quantity: a.balance, remaining: a.balance, startsAt: a.updatedAt, expiresAt: null, revoked: false, level: 1, benefits: [] });
    for (const e of history) entries.unshift({ id: crypto.randomUUID(), poolId: p.id, userId: a.userId, appId: a.appId, assetKey: "points", action: e.type === "debit" ? "consume" : "grant", quantity: e.delta, reason: e.note, referenceId: e.idempotencyKey, actor: "本地演示数据", createdAt: e.createdAt, balanceAfter: e.balanceAfter });
    own.createdAt = a.updatedAt;
  }
  for (const { membership: m } of seedMemberships) {
    const p = bound(m.appId, "membership"), own = owner(p.id, m.userId, m.level);
    own.createdAt = m.createdAt;
    lots.push({ poolId: p.id, userId: m.userId, assetKey: m.level, quantity: 1, remaining: m.status === "canceled" ? 0 : 1, startsAt: m.startedAt,
      expiresAt: m.expiresAt, revoked: m.status === "canceled", level: 1, benefits: [] });
    const e = post(own, m.appId, m.status === "canceled" ? "revoke" : "grant", 1, "演示会员结转"); e.createdAt = m.updatedAt;
  }
  const data: DevelopmentAssetData = { pools, products, bindings, accounts: () => clone(owners.map(account)), ledger: () => clone(entries), adjust };
  const membershipAssets = createAssetApi(options.getApps, data);
  const appInfo = async (appId: string) => {
    const app = options.getApp ? await options.getApp(appId) : { name: bindings.find((b) => b.appId === appId)?.appName ?? appId, code: appId };
    return { appName: app.name, appCode: app.code };
  };
  const history = (a: Owner) => entries.filter((e) => e.poolId === a.poolId && e.userId === a.userId);
  const pointDetail = async (appId: string, userId: string): Promise<c.AdminPointAccountDetail> => {
    const p = bound(appId, "points"), a = owners.find((a) => a.poolId === p.id && a.userId === userId && a.assetKey === "points");
    if (!a) throw new Error("积分账户不存在");
    const h = history(a), u = users.get(userId)!;
    return { account: { appId, userId, ...await appInfo(appId), userName: u.name, userPublicId: u.publicId,
      balance: account(a).available, version: Math.max(1, h.length), updatedAt: h[0]?.createdAt ?? a.createdAt },
      entries: h.map((e) => ({ id: e.id, appId, userId, type: e.action === "grant" ? "credit" : e.action === "consume" ? "debit" : "adjust",
        delta: e.quantity, balanceAfter: e.balanceAfter, idempotencyKey: `asset:${e.id}`, note: e.reason.slice(0, 200), createdAt: e.createdAt })) };
  };
  const points: PointApi = {
    async list(query = {}) {
      const q = c.adminPointAccountListQuerySchema.parse(query), rows: c.AdminPointAccount[] = [];
      for (const b of bindings.filter((b) => b.kind === "points" && (!q.appId || q.appId === b.appId) && pools.some((p) => p.id === b.poolId && p.enabled))) {
        for (const a of owners.filter((a) => a.poolId === b.poolId)) rows.push((await pointDetail(b.appId, a.userId)).account);
      }
      const filtered = rows.filter((a) => `${a.userName} ${a.userPublicId} ${a.appName} ${a.appCode}`.toLowerCase().includes(q.keyword.toLowerCase()));
      return { items: filtered.slice((q.page - 1) * q.pageSize, q.page * q.pageSize), total: filtered.length };
    },
    get: pointDetail,
    async adjust(input) {
      const v = c.adminAdjustPointsInputSchema.parse(input), p = bound(v.appId, "points");
      const key = `points:${v.appId}:${v.idempotencyKey}`, request = JSON.stringify(v);
      if (receipts.has(key)) { if (receipts.get(key) !== request) throw new Error("幂等键已用于其他参数"); return pointDetail(v.appId, v.userId); }
      await membershipAssets.adjust({ mutationId: crypto.randomUUID(), reason: v.note.length < 2 ? `调账：${v.note}` : v.note,
        appId: v.appId, userId: v.userId, poolId: p.id, assetKey: "points", action: v.delta > 0 ? "grant" : "debit", quantity: Math.abs(v.delta) });
      receipts.set(key, request);
      return pointDetail(v.appId, v.userId);
    },
  };
  const memberRows = async (): Promise<c.AdminMembership[]> => {
    const plans = (await configuration.listPlans()).items, rows: c.AdminMembership[] = [];
    for (const b of bindings.filter((b) => b.kind === "membership" && pools.some((p) => p.id === b.poolId && p.enabled))) {
      const userIds = [...new Set(owners.filter((a) => a.poolId === b.poolId).map((a) => a.userId))];
      for (const userId of userIds) {
        const candidates = owners.filter((a) => a.poolId === b.poolId && a.userId === userId).map((a) => ({ a, view: account(a) }))
          .sort((a, b) => Number(b.view.active) - Number(a.view.active) || b.view.level - a.view.level || a.a.assetKey.localeCompare(b.a.assetKey));
        const { a, view } = candidates[0]!, h = history(a), u = users.get(userId)!;
        const ownLots = lots.filter((l) => l.poolId === a.poolId && l.userId === userId && l.assetKey === a.assetKey);
        const status = view.active ? "active" : a.frozen || ownLots.every((l) => l.revoked) ? "canceled" : "expired";
        const plan = plans.find((p) => p.appId === b.appId && p.level === a.assetKey);
        rows.push({ id: oldIds.get(`${b.appId}:${userId}`) ?? `memb_${b.appId}_${userId}`, appId: b.appId, userId, ...await appInfo(b.appId),
          userName: u.name, userPublicId: u.publicId, member: view.active, status, level: a.assetKey, levelName: plan?.name ?? null, levelNameUg: plan?.nameUg ?? null,
          expiresAt: view.permanent ? null : view.expiresAt ?? ownLots.map((l) => l.expiresAt!).sort().at(-1) ?? null,
          startedAt: ownLots.map((l) => l.startsAt).sort()[0] ?? a.createdAt, canceledAt: status === "canceled" ? h[0]?.createdAt ?? a.createdAt : null,
          idempotencyKey: `asset:${a.poolId}`, version: Math.max(1, h.length), createdAt: a.createdAt, updatedAt: h[0]?.createdAt ?? a.createdAt });
      }
    }
    return rows;
  };
  const notifyMembership = (m: c.AdminMembership, eventKey: "membership.granted" | "membership.canceled") => {
    const at = now(), granted = eventKey === "membership.granted";
    const title = granted ? "会员已开通" : "会员已取消";
    const body = granted ? "当前应用会员已开通或续期。" : "当前应用会员已取消。";
    const titleUg = granted ? "ئەزالىق ئېچىلدى" : "ئەزالىق بىكار قىلىندى";
    const bodyUg = granted ? "نۆۋەتتىكى ئەپ ئەزالىقى ئېچىلدى ياكى ئۇزارتىلدى." : "نۆۋەتتىكى ئەپ ئەزالىقى بىكار قىلىندى.";
    const occurrenceKey = `${m.id}:${m.version}:${eventKey}`;
    const events = seededEvents.get(m.id) ?? [];
    events.push({ id: crypto.randomUUID(), membershipId: m.id, type: "notification", fromStatus: null, toStatus: m.status,
      eventKey, channel: "in_app", note: "站内信已记录，未发送短信", createdAt: at });
    seededEvents.set(m.id, events);
    options.inbox?.enqueue({ appId: m.appId, appName: m.appName, userId: m.userId, eventKey, occurrenceKey,
      category: "transactional", title, titleUg, body, bodyUg, createdAt: at });
    options.webhooks?.enqueue({ appId: m.appId, eventKey, occurrenceKey,
      payload: { event: eventKey, appId: m.appId, occurredAt: at,
        data: { membershipId: m.id, userId: m.userId, level: m.level, status: m.status, expiresAt: m.expiresAt } } });
  };
  const memberships: DevelopmentMembershipApi = {
    ...configuration,
    async list(query = {}) {
      const q = c.adminMembershipListQuerySchema.parse(query);
      const base = (await memberRows()).filter((r) => (!q.appId || q.appId === r.appId) && `${r.userName} ${r.userPublicId} ${r.appName} ${r.level}`.toLowerCase().includes(q.keyword.toLowerCase()));
      const filtered = base.filter((r) => q.status === "all" || r.status === q.status);
      return { items: filtered.slice((q.page - 1) * q.pageSize, q.page * q.pageSize), total: filtered.length,
        summary: { total: base.length, active: base.filter((r) => r.status === "active").length, expired: base.filter((r) => r.status === "expired").length, canceled: base.filter((r) => r.status === "canceled").length } };
    },
    async get(id) {
      const membership = (await memberRows()).find((m) => m.id === id);
      if (!membership) throw new Error("会员记录不存在");
      return { membership, events: clone(seededEvents.get(id) ?? []) };
    },
    async grant(input) {
      const v = c.adminGrantMembershipInputSchema.parse(input), p = bound(v.appId, "membership");
      const key = `membership:${v.appId}:${v.idempotencyKey}`, request = JSON.stringify(v);
      const firstGrant = !receipts.has(key);
      if (receipts.has(key)) {
        if (receipts.get(key) !== request) throw new Error("幂等键已用于其他参数");
      } else {
        const ownLots = lots.filter((l) => l.poolId === p.id && l.userId === v.userId && l.assetKey === v.level && !l.revoked);
        if (ownLots.some((l) => !l.expiresAt)) throw new Error("不能重复开通永久权益");
        const base = ownLots.reduce((end, l) => l.expiresAt! > end ? l.expiresAt! : end, now());
        if (v.expiresAt <= base) throw new Error("到期时间不能缩短已有权益");
        await membershipAssets.adjust({ mutationId: crypto.randomUUID(), reason: "开通或续期", appId: v.appId, userId: v.userId, poolId: p.id,
          assetKey: v.level, action: "grant", durationDays: Math.ceil((Date.parse(v.expiresAt) - Date.parse(base)) / 86400000),
          level: ownLots[0]?.level ?? 1, consumable: false });
        lots[lots.length - 1]!.expiresAt = v.expiresAt;
        receipts.set(key, request);
      }
      const row = (await memberRows()).find((m) => m.appId === v.appId && m.userId === v.userId)!;
      if (firstGrant) notifyMembership(row, "membership.granted");
      return memberships.get(row.id);
    },
    async cancel(id, input) {
      const { membership: m } = await memberships.get(id);
      if (m.version !== input.expectedVersion) throw new Error("会员记录已被更新，请刷新后重试");
      if (m.status === "canceled") throw new Error("会员已经取消");
      const p = bound(m.appId, "membership");
      for (const a of owners.filter((a) => a.poolId === p.id && a.userId === m.userId)) {
        await membershipAssets.adjust({ mutationId: crypto.randomUUID(), reason: "取消共享会员权益", appId: m.appId, userId: m.userId, poolId: p.id, assetKey: a.assetKey, action: "revoke" });
      }
      const result = await memberships.get(id);
      notifyMembership(result.membership, "membership.canceled");
      return memberships.get(id);
    },
    async fulfillFromPaidOrder(order) {
      const key = `order:${order.appId}:${order.id}`;
      if (receipts.has(key)) return;
      const level = String(order.payload.level ?? order.payload.planId ?? "");
      const plan = (await configuration.listPlans({ appId: order.appId })).items.find((p) => p.level === level);
      const days = Number(order.payload.days ?? order.payload.periodDays ?? plan?.periodDays);
      if (!/^[A-Za-z0-9_-]{1,32}$/.test(level) || !Number.isInteger(days) || days < 1) return;
      const p = bound(order.appId, "membership");
      const base = lots.filter((l) => l.poolId === p.id && l.userId === order.userId && l.assetKey === level && !l.revoked).reduce((end, l) => l.expiresAt && l.expiresAt > end ? l.expiresAt : end, now());
      await memberships.grant({ appId: order.appId, userId: order.userId, level, expiresAt: new Date(Date.parse(base) + days * 86400000).toISOString(), idempotencyKey: `order:${order.id}` });
      receipts.set(key, "fulfilled");
    },
  };
  return { membershipAssets, memberships, points };
}
